import { type NuxtAxiosInstance } from "@nuxtjs/axios";

import {
  type CreatorProductActivityServiceParams,
  type CreatorProductActivityServiceResponse,
  type CreatorProductPostServiceResponse,
  type CreatorProductSearchServiceParams,
  type CreatorProductSearchServiceResponse,
} from "@/store/creator-products/types";
import { CreatorProductRanking } from "@/store/creator-products/utils";
import { useApi } from "@/utils/api";
import { DEFAULT_API_RESPONSE_LIMIT, DEFAULT_SEARCH_SERVICE_ANALYTICS } from "@/utils/constants";

export default function useCreatorProductApi($axios: NuxtAxiosInstance) {
  // This gives us the product_ref_id and gallery_post_id
  const searchServiceBaseUrl = "/legacy/v2/search";
  const searchServiceApi = useApi({ name: "consumerSearchService", url: searchServiceBaseUrl, $axios });

  // This gives us actual product data needed for rendering the UI
  const postServiceBaseUrl = "/v3/posts";
  const postServiceApi = useApi({ name: "postService", url: postServiceBaseUrl, $axios });

  // This gives us the initial state for the recent products feed as well as pagination meta
  const activityServiceBaseUrl = "/v3/activities/products/profiles";
  const activityServiceApi = useApi({ name: "activityServiceV3", url: activityServiceBaseUrl, $axios });

  async function getCreatorTrendingProducts({
    profileId,
    page = 0,
    analytics = DEFAULT_SEARCH_SERVICE_ANALYTICS,
    ranking = CreatorProductRanking.Trending,
    filters = [],
    limit = DEFAULT_API_RESPONSE_LIMIT,
    context = ["include:gallery_products"], // this is necessary to get the product's gallery_post_id
  }: CreatorProductSearchServiceParams) {
    if (!profileId) {
      throw new Error("You must supply a profileId to getCreatorTrendingProducts()");
    }

    const searchServiceResponse = await searchServiceApi.request<CreatorProductSearchServiceResponse>({
      url: `${searchServiceBaseUrl}/shop/products`,
      method: "POST",
      responseType: "json",
      data: {
        profileId,
        page,
        analytics,
        ranking,
        filters,
        limit,
        context,
      },
    });

    const postId = searchServiceResponse.data?.hits[0]?.postId; // all hits will have the same postId as it's a "gallery post"
    const productIds = searchServiceResponse.data?.hits?.map((item) => item.productId) ?? []; // these are product_reference_id's

    if (!productIds.length) {
      return null;
    }

    // https://backstage.ltkoperator.com/catalog/default/api/ltk-post-service/definition#/External-V3/V3GetManyProductsByPost
    const postServiceResponse = await postServiceApi.request<CreatorProductPostServiceResponse>({
      url: `${postServiceBaseUrl}/${postId}/products`,
      method: "GET",
      params: {
        includes: ["media_objects", "product_references"],
        product_reference_ids: productIds,
      },
    });

    // We only return the data from postService; we do not need metadata for pagination as the search-service limits to (5) trending products
    return postServiceResponse.data?.products.length ? postServiceResponse : null;
  }

  async function getCreatorRecentProducts({
    profileId,
    next,
    limit = DEFAULT_API_RESPONSE_LIMIT,
  }: CreatorProductActivityServiceParams) {
    if (!profileId) {
      throw new Error("You must supply a profileId to getCreatorProductsFeed()");
    }

    const activitiesServiceResponse = await activityServiceApi.request<CreatorProductActivityServiceResponse>({
      url: `${activityServiceBaseUrl}/${profileId}`,
      method: "GET",
      responseType: "json",
      params: {
        next,
        limit,
      },
    });

    const postId = activitiesServiceResponse.data?.activities[0]?.contentId; // all hits will have the same contentId as it's a "gallery post"
    const productIds = activitiesServiceResponse.data?.activities?.map((activity) => activity.productRefId) ?? [];
    const activityServiceMeta = activitiesServiceResponse.data?.meta;

    if (!productIds.length) {
      return null;
    }

    const postServiceResponse = await postServiceApi.request<CreatorProductPostServiceResponse>({
      url: `${postServiceBaseUrl}/${postId}/products`,
      method: "GET",
      responseType: "json",
      params: {
        includes: ["media_objects", "product_references"],
        product_reference_ids: productIds,
      },
    });

    const postServiceResponseWithActivityServiceMeta = {
      ...postServiceResponse,
      data: { ...postServiceResponse.data, meta: activityServiceMeta },
    };

    // The meta from post-service is not useful data, so we overwrite it here to allow for pagination of the activities-service
    // This is necessary as we have to call the activities-service to get the postId + productId(s) to feed to the post-service
    // which returns actual product data, media objects, and product details
    return postServiceResponse.data?.products.length ? postServiceResponseWithActivityServiceMeta : null;
  }

  async function searchCreatorRecentProducts({
    profileId,
    query,
    page = 0,
    analytics = DEFAULT_SEARCH_SERVICE_ANALYTICS,
    ranking = CreatorProductRanking.Relevant,
    filters = [],
    limit = DEFAULT_API_RESPONSE_LIMIT,
    context = ["include:gallery_products"], // this is necessary to get the product's gallery_post_id
  }: CreatorProductSearchServiceParams) {
    if (!profileId || !query) {
      throw new Error("You must supply a profileId AND query to getCreatorTrendingProducts()");
    }

    const searchServiceResponse = await searchServiceApi.request<CreatorProductSearchServiceResponse>({
      url: `${searchServiceBaseUrl}/shop/products`,
      method: "POST",
      responseType: "json",
      data: {
        profileId,
        query,
        page,
        analytics,
        ranking,
        filters,
        limit,
        context,
      },
    });

    const postId = searchServiceResponse.data?.hits[0]?.postId; // all hits will have the same postId as it's a "gallery post"
    const productIds = searchServiceResponse.data?.hits?.map((item) => item.productId) ?? []; // these are product_reference_id's
    const searchServiceMeta = {
      page: searchServiceResponse.data?.page,
      nbHits: searchServiceResponse.data?.nbHits,
      nbPages: searchServiceResponse.data?.nbPages,
      length: searchServiceResponse.data?.length,
      offset: searchServiceResponse.data?.offset,
      nbSortedHits: searchServiceResponse.data?.nbSortedHits,
      hitsPerPage: searchServiceResponse.data?.hitsPerPage,
    };

    if (!productIds.length) {
      return null;
    }

    // https://backstage.ltkoperator.com/catalog/default/api/ltk-post-service/definition#/External-V3/V3GetManyProductsByPost
    const postServiceResponse = await postServiceApi.request<CreatorProductPostServiceResponse>({
      url: `${postServiceBaseUrl}/${postId}/products`,
      method: "GET",
      params: {
        includes: ["media_objects", "product_references"],
        product_reference_ids: productIds,
      },
    });

    const postServiceResponseWithSearchServiceMeta = {
      ...postServiceResponse,
      data: { ...postServiceResponse.data, meta: searchServiceMeta },
    };

    // The meta from post-service is not useful data, so we overwrite it here to allow for pagination of the search-service
    // This is necessary as we have to call the search-service to get the postId + productId(s) to feed to the post-service
    // which returns actual product data, media objects, and product details
    return postServiceResponse.data?.products.length ? postServiceResponseWithSearchServiceMeta : null;
  }

  return {
    getCreatorTrendingProducts,
    getCreatorRecentProducts,
    searchCreatorRecentProducts,
  };
}
